import {
  Box,
  Chip,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useEffect, useMemo } from 'react';
import { CompanyService } from '../../../service/CompanyService';
import { CompanySpacesService } from '../../../service/CompanySpacesService';
import { UserService } from '../../../service/UserService';
import { useAuthStore } from '../../../store/storeAuth';
import useFlowsStore from '../../../store/storeFlows';
import {
  findCommandTextModelCapabilities,
  getCommandTextReasoningLockedOn,
  getCommandTextReasoningVisible,
  getCommandTextWebSearchVisible,
} from '../../../util/commandTextModelFeatures';
import {
  AiTextGeneratorFormValues,
  getDeprecatedModelMigration,
  systemDisplayNameMap,
  systemValueMap,
} from '../../AiConfigs/constants';
import KitModal from '../../UI/KitModal';
import Anthropic from '../Anthropic';
import Gemini from '../Gemini';
import GroqAi from '../GroqAi';
import OpenAi from '../OpenAi';
import OpenSources from '../OpenSources';
import Perplexity from '../Perplexity';
import XAi from '../XAi';

function getCommandTextModelIdForUi(
  formData: AiTextGeneratorFormValues,
): string {
  const map: Record<string, keyof AiTextGeneratorFormValues> = {
    OpenAI: 'aiModel',
    anthropicAi: 'anthropicModel',
    Gemini: 'geminiModel',
    XAi: 'xaiModel',
    Groq: 'groqModel',
  };
  const field = map[formData.AiSystem];
  return field ? (formData[field] as string) || '' : '';
}

type Props = {
  onHide: () => void;
  show: boolean;
  formData: AiTextGeneratorFormValues;
  onChange: (e: any) => void;
  nodeData?: any;
};

const SectionTitle = ({ children }: { children: string }) => {
  const { user } = useAuthStore(state => state);
  return (
    <>
      <Typography
        fontSize="14px"
        variant="h6"
        fontWeight={600}
        color={user?.theme === 'dark' ? '#D0D5DD' : 'black'}
      >
        {children}
      </Typography>
    </>
  );
};

const SettingsModal = ({
  onHide,
  show,
  formData,
  onChange,
  nodeData,
}: Props) => {
  const {
    user,
    technologies,
    companySpaceTechnologies,
    setCompanySpaceTechnologies,
    setTechnologies,
  } = useAuthStore(state => state);
  const { spaceId } = useFlowsStore();
  const isDark = user?.theme === 'dark';

  useEffect(() => {
    if (!show) return;
    const fetchAllTechnologies = async () => {
      try {
        if (spaceId) {
          const res = await CompanySpacesService.getSpaceTechnologies(spaceId as string);
          setCompanySpaceTechnologies(res.data);
        }
        if (technologies.length === 0) {
          const technologiesData = user?.selectedCompanyId
            ? await CompanyService.getCompanyTechnologies(user.selectedCompanyId)
            : await UserService.getTechnologies();
          setTechnologies(technologiesData.data);
        }
      } catch (error) {
        console.error('Error fetching technologies:', error);
      }
    };
    fetchAllTechnologies();
  }, [spaceId, show, user?.selectedCompanyId]);

  // const textSystems = ['anthropic', 'openAi', 'groq', 'gemini', 'perplexity', 'replicate', 'xai'];
  const textSystems = ['anthropic', 'openAi', 'groq', 'gemini', 'perplexity', 'xai'];

  const filteredTextSystems = useMemo(() => {
    const systemsToUse =
      spaceId && companySpaceTechnologies.length > 0
        ? companySpaceTechnologies
        : technologies;
    return (
      systemsToUse
        ?.filter((system: any) => textSystems.includes(system?.system))
        ?.map((system: any) => ({
          ...system,
          models: system?.models?.filter(
            (model: any) => model.type === 'Text' && !model.isDeprecated,
          ),
        })) || []
    );
  }, [spaceId, companySpaceTechnologies, technologies]);

  const commandTextCapabilityUi = useMemo(() => {
    const eligible = ['OpenAI', 'anthropicAi', 'Gemini', 'XAi', 'Groq'];
    if (!eligible.includes(formData.AiSystem)) return null;
    const modelId = getCommandTextModelIdForUi(formData);
    if (!modelId) return null;
    const capabilities = findCommandTextModelCapabilities(
      filteredTextSystems,
      formData.AiSystem,
      modelId,
    );
    if (!capabilities) {
      return null;
    }
    return {
      showReasoning: getCommandTextReasoningVisible(capabilities),
      reasoningLockedOn: getCommandTextReasoningLockedOn(capabilities),
      showWebSearch: getCommandTextWebSearchVisible(capabilities),
    };
  }, [
    filteredTextSystems,
    formData.AiSystem,
    formData.aiModel,
    formData.anthropicModel,
    formData.geminiModel,
    formData.xaiModel,
    formData.groqModel,
  ]);

  useEffect(() => {
    if (
      commandTextCapabilityUi?.showReasoning &&
      commandTextCapabilityUi.reasoningLockedOn &&
      formData.reasoning !== true
    ) {
      onChange({ target: { name: 'reasoning', value: true } });
      return;
    }
    if (
      commandTextCapabilityUi &&
      !commandTextCapabilityUi.showReasoning &&
      formData.reasoning === true
    ) {
      onChange({ target: { name: 'reasoning', value: false } });
    }
  }, [
    commandTextCapabilityUi?.showReasoning,
    commandTextCapabilityUi?.reasoningLockedOn,
    formData.reasoning,
  ]);

  useEffect(() => {
    if (
      commandTextCapabilityUi &&
      !commandTextCapabilityUi.showWebSearch &&
      formData.isWebEnabled === true
    ) {
      onChange({ target: { name: 'isWebEnabled', value: false } });
    }
  }, [commandTextCapabilityUi?.showWebSearch, formData.isWebEnabled]);

  useEffect(() => {
    if (!show) return;
    if (filteredTextSystems.length === 0) return;
    const systemsToUse =
      spaceId && companySpaceTechnologies.length > 0
        ? companySpaceTechnologies
        : technologies;
    const migration = getDeprecatedModelMigration(formData.AiSystem, formData, systemsToUse);
    if (migration) {
      onChange({ target: { name: migration.fieldName, value: migration.newModel } });
    }
  }, [show, technologies, companySpaceTechnologies, filteredTextSystems.length, formData.AiSystem]);

  const selectSx = {
    width: '100%',
    '& .MuiList-root': { backgroundColor: isDark ? '#1D2939' : '#F9FAFB' },
    bgcolor: isDark ? '#1D2939' : '#F9FAFB',
    color: isDark ? '#D0D5DD' : '#101828',
    border: `2px solid ${isDark ? '#475467' : '#EAECF0'}`,
  };

  const menuProps = {
    PaperProps: {
      sx: {
        '& .Mui-selected': { backgroundColor: isDark ? '#667085' : '#D0D5DD' },
        backgroundColor: isDark ? '#1D2939' : '#F9FAFB',
      },
    },
  };

  return (
    <KitModal
      fullWidth
      sx={{
        '& .MuiPaper-root': { maxWidth: '650px', borderRadius: '16px' },
        '& .MuiDialogContent-root': { padding: '16px' },
        '& .MuiDialogActions-root': { padding: '16px' },
      }}
      onHide={onHide}
      show={show}
      title="Advanced Settings"
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px', paddingRight: '8px', paddingLeft: '8px' }}>
        <Typography
          fontSize="14px"
          variant="h6"
          fontWeight={600}
          color={isDark ? '#D0D5DD' : 'black'}
        >
          Provider & Model
        </Typography>

        <FormControl sx={{ width: '100%' }}>
          <InputLabel id="ai-system-label">AI System</InputLabel>
          <Select
            name="AiSystem"
            labelId="ai-system-label"
            label="AI System"
            value={formData.AiSystem}
            onChange={onChange}
            sx={selectSx}
            MenuProps={menuProps}
          >
            {filteredTextSystems?.map((opt: any, index: number) => {
              const disabled = opt?.models?.every((model: any) => model.enabled === false);
              const disabledByCompany = opt?.models?.every((model: any) => model.disabledByCompany);
              const systemValue = systemValueMap[opt.system] || opt.system;
              const displayName = systemDisplayNameMap[systemValue] || systemValue;
              return (
                <MenuItem key={index} value={systemValue} disabled={disabled}>
                  <Box sx={{ display: 'flex', width: '100%', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography>{displayName}</Typography>
                    {disabledByCompany && (
                      <Chip label="Disabled by admin" color="error" size="small" variant="filled" sx={{ borderRadius: '8px' }} />
                    )}
                  </Box>
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>

        {formData.AiSystem === 'OpenAI' && (
          <OpenAi systems={filteredTextSystems} onChange={onChange} watch={formData} tokenCount={0} />
        )}
        {formData.AiSystem === 'anthropicAi' && (
          <Anthropic systems={filteredTextSystems} onChange={onChange} watch={formData} />
        )}
        {formData.AiSystem === 'Gemini' && (
          <Gemini imageSystem watch={formData} onChange={onChange} systems={filteredTextSystems} />
        )}
        {formData.AiSystem === 'Groq' && (
          <GroqAi onChange={onChange} watch={formData} systems={filteredTextSystems} />
        )}
        {formData.AiSystem === 'perplexityAi' && (
          <Perplexity systems={filteredTextSystems} onChange={onChange} watch={formData} />
        )}
        {formData.AiSystem === 'XAi' && (
          <XAi systems={filteredTextSystems} onChange={onChange} watch={formData} tokenCount={0} />
        )}

        {(commandTextCapabilityUi && commandTextCapabilityUi.showReasoning) && (
          <>
            <SectionTitle>Model Features</SectionTitle>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {commandTextCapabilityUi.showReasoning && (
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.reasoning !== false}
                      disabled={commandTextCapabilityUi.reasoningLockedOn}
                      onChange={ev => {
                        onChange({ target: { name: 'reasoning', value: ev.target.checked } });
                      }}
                      name="reasoning"
                    />
                  }
                  label="Reasoning"
                />
              )}
              {commandTextCapabilityUi.showWebSearch && (
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.isWebEnabled !== false}
                      onChange={ev => {
                        onChange({ target: { name: 'isWebEnabled', value: ev.target.checked } });
                      }}
                      name="isWebEnabled"
                    />
                  }
                  label="Web Search"
                />
              )}
            </Box>
          </>
        )}

        <SectionTitle>Multimodal</SectionTitle>
        <FormControlLabel
          control={
            <Switch
              checked={nodeData?.isEmptyState ? true : !!formData.multimodalEnabled}
              onChange={e => {
                onChange({ target: { name: 'multimodalEnabled', value: e.target.checked } });
              }}
              name="multimodalEnabled"
            />
          }
          label="Enable Multimodal AI Models"
        />

        {/* <SectionTitle>Streaming</SectionTitle>
        <FormControlLabel
          control={
            <Switch
              checked={formData.streamEnabled}
              onChange={e => {
                onChange({ target: { name: 'streamEnabled', value: e.target.checked } });
              }}
              name="streamEnabled"
            />
          }
          label="Stream Enabled"
        /> */}

        {/* <FormControl disabled={!formData.streamEnabled} sx={{ width: '100%' }}>
          <InputLabel id="stream-type-label">Stream Type</InputLabel>
          <Select
            name="streamType"
            labelId="stream-type-label"
            label="Stream Type"
            value={formData.streamType}
            onChange={onChange}
            sx={selectSx}
            MenuProps={menuProps}
          >
            <MenuItem value="chat">Chat</MenuItem>
            <MenuItem value="form">Form</MenuItem>
            <MenuItem value="api_callback">API Callback</MenuItem>
          </Select>
        </FormControl> */}

        {formData.streamType === 'api_callback' && (
          <TextField
            name="apiCallbackUrl"
            label="API Callback URL"
            value={formData.apiCallbackUrl}
            onChange={onChange}
          />
        )}
      </Box>
    </KitModal>
  );
};

export default SettingsModal;
